import { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Paper, Divider,
  CircularProgress, Stepper, Step, StepLabel, StepContent,
  Chip, IconButton, InputAdornment
} from '@mui/material';
import { WhatsApp, Save, Visibility, VisibilityOff, CheckCircle, ContentCopy } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function WhatsAppConfig() {
  const [config, setConfig] = useState({ phone_number_id: '', access_token: '', webhook_verify_token: '', numero_display: '', activo: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showToken, setShowToken] = useState(false);

  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await axios.get(`${API}/api/whatsapp/config`, { headers: headers() });
      if (res.data.data) setConfig(prev => ({ ...prev, ...res.data.data }));
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando configuración');
    } finally { setLoading(false); }
  }

  async function handleSave() {
    if (!config.phone_number_id || !config.webhook_verify_token) {
      setError('Phone Number ID y Webhook Verify Token son requeridos');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/whatsapp/config`, config, { headers: headers() });
      setSuccess('Configuración guardada correctamente');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando');
    } finally { setSaving(false); }
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ bgcolor: '#25d366', borderRadius: 2, p: 1, display: 'flex' }}>
          <WhatsApp sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>WhatsApp Business</Typography>
          <Typography variant="body2" color="text.secondary">Conecta tu número de WhatsApp Business para recibir y enviar mensajes</Typography>
        </Box>
        {config.phone_number_id && (
          <Chip label="Configurado" color="success" size="small" icon={<CheckCircle />} sx={{ ml: 'auto' }} />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Instrucciones paso a paso */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8fff8', border: '1px solid #c8e6c9' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>📋 Pasos para configurar</Typography>
        <Stepper orientation="vertical" nonLinear>
          {[
            {
              label: 'Crear aplicación en Meta for Developers',
              content: 'Ve a developers.facebook.com → Mis Apps → Crear App → Empresa → Agrega el producto WhatsApp'
            },
            {
              label: 'Obtener Phone Number ID',
              content: 'En tu app Meta, ve a WhatsApp → Configuración de la API → Copia el "Phone number ID" del número de prueba o tu número verificado'
            },
            {
              label: 'Generar Access Token',
              content: 'En WhatsApp → Configuración de la API → Token de acceso temporal (o genera uno permanente con un System User)'
            },
            {
              label: 'Configurar Webhook',
              content: `En WhatsApp → Configuración → Webhooks → URL de devolución de llamada: ${webhookUrl}`
            },
            {
              label: 'Suscribir campos del webhook',
              content: 'En el webhook, suscríbete al campo "messages" para recibir mensajes entrantes'
            },
          ].map((step, i) => (
            <Step key={i} active expanded>
              <StepLabel><Typography variant="body2" fontWeight={600}>{step.label}</Typography></StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{step.content}</Typography>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Webhook URL para copiar */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f3f4f6' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>URL del Webhook (configura esto en Meta)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>{webhookUrl}</Typography>
          <IconButton size="small" onClick={() => copyToClipboard(webhookUrl)}><ContentCopy fontSize="small" /></IconButton>
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Formulario */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Credenciales de la API</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <TextField
          label="Phone Number ID *"
          value={config.phone_number_id}
          onChange={e => setConfig(p => ({ ...p, phone_number_id: e.target.value }))}
          fullWidth
          helperText="Ejemplo: 123456789012345"
          placeholder="Encontrado en Meta for Developers → WhatsApp → Configuración de la API"
        />
        <TextField
          label="Access Token *"
          type={showToken ? 'text' : 'password'}
          value={config.access_token}
          onChange={e => setConfig(p => ({ ...p, access_token: e.target.value }))}
          fullWidth
          helperText="Token temporal de prueba o token permanente del System User"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowToken(p => !p)}>
                  {showToken ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          label="Webhook Verify Token *"
          value={config.webhook_verify_token}
          onChange={e => setConfig(p => ({ ...p, webhook_verify_token: e.target.value }))}
          fullWidth
          helperText="Token secreto que tú defines para verificar el webhook en Meta"
          placeholder="Ejemplo: mi-token-secreto-2026"
        />
        <TextField
          label="Número de WhatsApp (display)"
          value={config.numero_display}
          onChange={e => setConfig(p => ({ ...p, numero_display: e.target.value }))}
          fullWidth
          placeholder="+593 99 123 4567"
          helperText="Formato de visualización del número (opcional)"
        />

        <Button variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave} disabled={saving || !config.phone_number_id || !config.webhook_verify_token}
          sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, alignSelf: 'flex-start', px: 4 }}>
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
}
